import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import TappaMap from "@/components/TappaMap";
import SEO from "@/components/SEO";
import ImageCredit from "@/components/ImageCredit";
import { difficultyLabel } from "@/i18n";
import { ArrowLeft, ArrowRight, MapPin, Mountain, Footprints, Bike, Landmark, Route, Info } from "lucide-react";

const TappaDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [tappa, setTappa] = useState(null);
  const [all, setAll] = useState([]);

  useEffect(() => {
    api.get(`/tappe/${slug}`).then((r) => setTappa(r.data)).catch(() => {});
    api.get("/tappe").then((r) => setAll(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  if (!tappa) return <div className="pt-40 text-center text-grafite-soft">Loading...</div>;

  const inSection = all.filter((x) => x.section === tappa.section);
  const idx = inSection.findIndex((x) => x.slug === slug);
  const prev = idx > 0 ? inSection[idx - 1] : null;
  const next = idx >= 0 && idx < inSection.length - 1 ? inSection[idx + 1] : null;

  return (
    <div data-testid={`tappa-detail-${slug}`}>
      <SEO
        titleIt={`Tappa ${String(tappa.order).padStart(2, "0")} · ${tappa.it.title} — ${tappa.km} km`}
        titleEn={`Stage ${String(tappa.order).padStart(2, "0")} · ${tappa.en.title} — ${tappa.km} km`}
        descriptionIt={`${tappa.it.subtitle} · ${tappa.km} km, ${tappa.elevation_gain_m} m di dislivello, a piedi ${tappa.walk_hours}${tappa.bike_hours ? `, in bici ${tappa.bike_hours}` : ""}. Da ${tappa.start_station} a ${tappa.end_station}.`}
        descriptionEn={`${tappa.en.subtitle} · ${tappa.km} km, ${tappa.elevation_gain_m} m elevation gain, walking ${tappa.walk_hours}${tappa.bike_hours ? `, cycling ${tappa.bike_hours}` : ""}. From ${tappa.start_station} to ${tappa.end_station}.`}
        image={tappa.image}
        type="article"
      />
      <section className="relative pt-40 pb-20 min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img loading="lazy" decoding="async" src={tappa.image} alt="" style={{ objectPosition: tappa.image_pos || "50% 50%" }} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-grafite via-grafite/40 to-grafite/20" />
          <ImageCredit src={tappa.image} variant={tappa.image_credit} size="lg" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 w-full text-pietra-light">
          <Link to="/tappe" className="inline-flex items-center gap-2 text-miele mb-6 hover:text-pietra-light">
            <ArrowLeft size={16} /> {t("nav_tappe")}
          </Link>
          <div className="font-serif text-6xl text-miele mb-4">Tappa {String(tappa.order).padStart(2, "0")}</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-none mb-4">{tappa[lang].title}</h1>
          <p className="text-xl md:text-2xl italic text-pietra-light/85 max-w-3xl">{tappa[lang].subtitle}</p>
        </div>
      </section>

      <section className="py-20 bg-pietra-light">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-16">
          <div className="md:col-span-2 space-y-8">
            <div className="flex items-center gap-4 flex-wrap">
              <StatusBadge status={tappa.status} />
              <span className="text-xs uppercase tracking-widest text-grafite-soft">{difficultyLabel(tappa.difficulty, lang)}</span>
              <span className="text-xs uppercase tracking-widest text-carrubo flex items-center gap-1">
                {tappa.type === "pedestrian" ? <><Footprints size={12} /> {t("tappa_type_walk")}</> : <><Route size={12} /> {t("tappa_type_both")}</>}
              </span>
            </div>
            <p className="text-lg text-grafite leading-relaxed">{tappa[lang].description}</p>

            <div>
              <div className="small-caps text-miele mb-4">{t("tappa_highlights")}</div>
              <ul className="space-y-3">
                {tappa[lang].highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 text-grafite">
                    <MapPin size={16} className="text-carrubo mt-1 flex-shrink-0" /><span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            {tappa[lang].notes && (
              <div className="p-5 bg-miele/15 border-l-4 border-miele rounded-sm flex gap-3">
                <Info size={20} className="text-miele-dark flex-shrink-0 mt-1" />
                <div>
                  <div className="small-caps text-miele-dark mb-2">{t("tappa_notes")}</div>
                  <p className="text-sm text-grafite leading-relaxed">{tappa[lang].notes}</p>
                </div>
              </div>
            )}
          </div>

          <aside className="bg-pietra p-8 rounded-sm border border-carrubo/15 h-fit space-y-6">
            <div>
              <div className="small-caps text-miele mb-6">Dati</div>
              <ul className="space-y-4 text-sm">
                <li className="flex items-center justify-between border-b border-carrubo/10 pb-3">
                  <span className="text-grafite-soft flex items-center gap-2"><MapPin size={14} />{t("tappa_km")}</span>
                  <span className="font-serif text-2xl text-carrubo">{tappa.km} km</span>
                </li>
                <li className="flex items-center justify-between border-b border-carrubo/10 pb-3">
                  <span className="text-grafite-soft flex items-center gap-2"><Mountain size={14} />{t("tappa_elevation")}</span>
                  <span className="font-semibold">↑ {tappa.elevation_gain_m} m</span>
                </li>
                <li className="flex items-center justify-between border-b border-carrubo/10 pb-3">
                  <span className="text-grafite-soft flex items-center gap-2"><Footprints size={14} />{t("tappa_walk")}</span>
                  <span className="font-semibold">{tappa.walk_hours}</span>
                </li>
                {tappa.bike_hours && (
                  <li className="flex items-center justify-between border-b border-carrubo/10 pb-3">
                    <span className="text-grafite-soft flex items-center gap-2"><Bike size={14} />{t("tappa_bike")}</span>
                    <span className="font-semibold">{tappa.bike_hours}</span>
                  </li>
                )}
                {tappa.ebike_hours && (
                  <li className="flex items-center justify-between border-b border-carrubo/10 pb-3">
                    <span className="text-grafite-soft flex items-center gap-2"><Bike size={14} className="text-miele" />{t("tappa_ebike")}</span>
                    <span className="font-semibold text-miele-dark">{tappa.ebike_hours}</span>
                  </li>
                )}
                <li className="pt-1">
                  <div className="small-caps text-grafite-soft mb-1">{t("tappa_start")}</div>
                  <div className="font-semibold text-sm mb-3">{tappa.start_station}</div>
                  <div className="small-caps text-grafite-soft mb-1">{t("tappa_end")}</div>
                  <div className="font-semibold text-sm">{tappa.end_station}</div>
                </li>
              </ul>
            </div>
            {tappa.surface && (
              <div className="pt-4 border-t border-carrubo/10 text-xs space-y-2">
                <div><span className="small-caps text-grafite-soft">{t("tappa_surface")}:</span> <span className="text-grafite">{tappa.surface}</span></div>
                <div><span className="small-caps text-grafite-soft">{t("tappa_traffic")}:</span> <span className="text-grafite">{tappa.traffic}</span></div>
              </div>
            )}
          </aside>
        </div>
      </section>

      {tappa.video && (
        <section data-testid="tappa-video" className="py-16 bg-grafite">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="small-caps text-miele mb-4">{lang === "it" ? "Video" : "Video"}</div>
            <div className="relative w-full rounded-sm overflow-hidden bg-black" style={{ aspectRatio: "16 / 9" }}>
              <video
                data-testid="tappa-video-player"
                src={tappa.video}
                controls
                preload="metadata"
                playsInline
                className="absolute inset-0 w-full h-full"
              >
                {lang === "it"
                  ? "Il tuo browser non supporta il video HTML5."
                  : "Your browser does not support HTML5 video."}
              </video>
            </div>
          </div>
        </section>
      )}

      {tappa.routes?.length > 0 && (
        <section data-testid="tappa-routes" className="py-20 bg-pietra-light">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="eyebrow text-miele mb-4">{lang === "it" ? "I due percorsi" : "The two routes"}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-grafite mb-14 max-w-3xl">
              {lang === "it"
                ? "Pantalica a Piedi comprende due anelli escursionistici distinti."
                : "Pantalica on Foot comprises two distinct walking loops."}
            </h2>

            <div className="space-y-20">
              {tappa.routes.map((r) => (
                <article key={r.id} data-testid={`route-${r.id}`} className="grid md:grid-cols-3 gap-10">
                  <div className="md:col-span-2 space-y-6">
                    <h3 className="font-serif text-3xl md:text-4xl text-grafite leading-tight">{r[lang].title}</h3>
                    <p className="text-grafite leading-relaxed">{r[lang].description}</p>

                    <div>
                      <div className="small-caps text-miele mb-3">{t("tappa_highlights")}</div>
                      <ul className="space-y-2">
                        {r[lang].highlights.map((h, i) => (
                          <li key={i} className="flex items-start gap-3 text-grafite text-sm">
                            <MapPin size={14} className="text-carrubo mt-1 flex-shrink-0" />
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {r[lang].notes && (
                      <div className="p-4 bg-miele/15 border-l-4 border-miele rounded-sm flex gap-3">
                        <Info size={18} className="text-miele-dark flex-shrink-0 mt-1" />
                        <p className="text-sm text-grafite leading-relaxed">{r[lang].notes}</p>
                      </div>
                    )}

                    {r.waypoints && (
                      <div className="pt-4">
                        <div className="small-caps text-miele mb-3">{t("tappa_map")}</div>
                        <TappaMap waypoints={r.waypoints} height="360px" />
                      </div>
                    )}
                  </div>

                  <aside className="bg-pietra p-6 rounded-sm border border-carrubo/15 h-fit space-y-4 text-sm">
                    <div className="small-caps text-miele mb-2">Dati</div>
                    <div className="flex items-center justify-between border-b border-carrubo/10 pb-2">
                      <span className="text-grafite-soft flex items-center gap-2"><MapPin size={14} />{t("tappa_km")}</span>
                      <span className="font-serif text-xl text-carrubo">{r.km} km</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-carrubo/10 pb-2">
                      <span className="text-grafite-soft flex items-center gap-2"><Mountain size={14} />{t("tappa_elevation")}</span>
                      <span className="font-semibold">↑ {r.elevation_gain_m} m</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-carrubo/10 pb-2">
                      <span className="text-grafite-soft flex items-center gap-2"><Footprints size={14} />{t("tappa_walk")}</span>
                      <span className="font-semibold">{r.walk_hours}</span>
                    </div>
                    <div className="pt-2">
                      <div className="small-caps text-grafite-soft mb-1">{t("tappa_start")}</div>
                      <div className="font-semibold text-sm mb-2">{r.start_station}</div>
                      <div className="small-caps text-grafite-soft mb-1">{t("tappa_end")}</div>
                      <div className="font-semibold text-sm">{r.end_station}</div>
                    </div>
                    {r.surface && (
                      <div className="pt-3 border-t border-carrubo/10 text-xs space-y-1">
                        <div><span className="small-caps text-grafite-soft">{t("tappa_surface")}:</span> <span className="text-grafite">{r.surface}</span></div>
                        <div><span className="small-caps text-grafite-soft">{t("tappa_traffic")}:</span> <span className="text-grafite">{r.traffic}</span></div>
                      </div>
                    )}
                  </aside>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {tappa.waypoints && !tappa.routes?.length && (
        <section className="py-16 bg-pietra">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="small-caps text-miele mb-4">{t("tappa_map")}</div>
            <TappaMap waypoints={tappa.waypoints} height="450px" />
          </div>
        </section>
      )}

      {tappa.unesco_sites?.length > 0 && (
        <section className="py-16 bg-carrubo text-pietra-light">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="eyebrow text-miele mb-4">{t("tappa_unesco")}</div>
            <div className="flex flex-wrap gap-4">
              {tappa.unesco_sites.map((slug) => (
                <Link key={slug} to={`/territorio/${slug}`}
                  className="inline-flex items-center gap-3 border border-miele/40 hover:bg-miele hover:text-grafite px-6 py-4 rounded-sm transition-all">
                  <Landmark size={18} />
                  <span className="font-serif text-xl capitalize">{slug.replace(/-/g, " ")}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 bg-pietra-light">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-4">
          {prev ? (
            <Link to={`/tappe/${prev.slug}`} data-testid="tappa-prev" className="p-6 bg-pietra rounded-sm border border-carrubo/10 hover:border-carrubo">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-miele mb-2">
                <ArrowLeft size={14} /> {t("tappa_prev")}
              </div>
              <div className="font-serif text-2xl text-grafite">{prev[lang].title}</div>
            </Link>
          ) : <div />}
          {next ? (
            <Link to={`/tappe/${next.slug}`} data-testid="tappa-next" className="p-6 bg-pietra rounded-sm border border-carrubo/10 hover:border-carrubo md:text-right">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-miele mb-2 md:justify-end">
                {t("tappa_next")} <ArrowRight size={14} />
              </div>
              <div className="font-serif text-2xl text-grafite">{next[lang].title}</div>
            </Link>
          ) : <div />}
        </div>
      </section>
    </div>
  );
};

export default TappaDetail;
